/**
 * The local mirror of the user's workspace (IndexedDB via Dexie). Every screen reads from
 * here, so the app is instant and fully usable offline. Writes go to these tables and to
 * the outbox; src/sync pushes the outbox to Supabase and pulls changes back.
 */
import Dexie, { type Table } from 'dexie';
import type {
  Checklist,
  ChecklistItem,
  Folder,
  List,
  NotificationLog,
  Profile,
  Reminder,
  Space,
  Status,
  Tag,
  Task,
  TaskTag,
  Workspace,
} from '@karya/core';

export interface TaskActivity {
  id: string;
  user_id: string;
  task_id: string;
  kind:
    | 'created'
    | 'status'
    | 'priority'
    | 'due'
    | 'name'
    | 'completed'
    | 'reopened'
    | 'moved'
    | 'reminder_sent'
    | 'recurrence_created';
  from_value: string | null;
  to_value: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export type SyncTable =
  | 'profiles'
  | 'workspaces'
  | 'spaces'
  | 'folders'
  | 'lists'
  | 'statuses'
  | 'tags'
  | 'tasks'
  | 'task_tags'
  | 'checklists'
  | 'checklist_items'
  | 'reminders'
  | 'notifications_log'
  | 'task_activity';

/** Tables the client writes to (task_activity is server-written only). */
export const WRITABLE_TABLES: SyncTable[] = [
  'profiles',
  'workspaces',
  'spaces',
  'folders',
  'lists',
  'statuses',
  'tags',
  'tasks',
  'task_tags',
  'checklists',
  'checklist_items',
  'reminders',
  'notifications_log',
];

/** Pull order: parents before children so foreign keys resolve in the UI. */
export const PULL_ORDER: SyncTable[] = [
  'profiles',
  'workspaces',
  'spaces',
  'folders',
  'lists',
  'statuses',
  'tags',
  'tasks',
  'task_tags',
  'checklists',
  'checklist_items',
  'reminders',
  'notifications_log',
  'task_activity',
];

export interface OutboxEntry {
  seq?: number;
  table: SyncTable;
  /** Row key: the id, or `task_id:tag_id` for task_tags. */
  key: string;
  /** upsert = full row (insert or replace); patch = changed fields of an existing row. */
  op: 'upsert' | 'patch';
  payload: Record<string, unknown>;
  /** server updated_at the local edit was based on (for "updated elsewhere"). */
  base_updated_at: string | null;
  created_at: string;
  attempts: number;
  inflight: 0 | 1;
  last_error: string | null;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export class KaryaDB extends Dexie {
  profiles!: Table<Profile, string>;
  workspaces!: Table<Workspace, string>;
  spaces!: Table<Space, string>;
  folders!: Table<Folder, string>;
  lists!: Table<List, string>;
  statuses!: Table<Status, string>;
  tags!: Table<Tag, string>;
  tasks!: Table<Task, string>;
  task_tags!: Table<TaskTag, [string, string]>;
  checklists!: Table<Checklist, string>;
  checklist_items!: Table<ChecklistItem, string>;
  reminders!: Table<Reminder, string>;
  notifications_log!: Table<NotificationLog, string>;
  task_activity!: Table<TaskActivity, string>;
  outbox!: Table<OutboxEntry, number>;
  meta!: Table<MetaRow, string>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      profiles: 'id',
      workspaces: 'id',
      spaces: 'id, workspace_id',
      folders: 'id, space_id',
      lists: 'id, space_id, folder_id',
      statuses: 'id, space_id, list_id',
      tags: 'id, space_id',
      tasks:
        'id, list_id, parent_task_id, status_id, due_date, completed_at, updated_at, recurrence_parent_id',
      task_tags: '[task_id+tag_id], task_id, tag_id',
      checklists: 'id, task_id',
      checklist_items: 'id, checklist_id',
      reminders: 'id, task_id, state, fire_at',
      notifications_log: 'id, task_id, reminder_id, sent_at, read_at',
      task_activity: 'id, task_id, created_at',
      outbox: '++seq, [table+key], inflight',
      meta: 'key',
    });
  }

  table_(name: SyncTable): Table<Record<string, unknown>, unknown> {
    return this.table(name) as unknown as Table<Record<string, unknown>, unknown>;
  }
}

let current: KaryaDB | null = null;
const listeners = new Set<() => void>();

/** Open (or switch to) the database for a user. Demo mode uses "karya-demo". */
export function openDb(userKey: string): KaryaDB {
  const name = `karya-${userKey}`;
  if (current?.name === name) return current;
  current?.close();
  current = new KaryaDB(name);
  listeners.forEach((l) => l());
  return current;
}

export function db(): KaryaDB {
  if (!current) throw new Error('Database not opened yet');
  return current;
}

export function hasDb(): boolean {
  return current !== null;
}

export function onDbChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await db().meta.get(key);
  return row?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db().meta.put({ key, value });
}

/** Row key used by the outbox and sync for a row of `table`. */
export function rowKey(table: SyncTable, row: Record<string, unknown>): string {
  return table === 'task_tags'
    ? `${row.task_id as string}:${row.tag_id as string}`
    : (row.id as string);
}
